import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";

interface Props {
  children: ReactNode;
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: string | null;
  retryCount: number;
}

/**
 * Enhanced ErrorBoundary that handles dynamic import failures and chunk load errors
 * Provides automatic retry logic with user feedback
 */
export class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      errorInfo: errorInfo.componentStack || null,
    });

    // Check if it's a chunk load error
    if (this.isChunkLoadError(error)) {
      console.warn("Chunk load error detected. This may be due to a deployment update.");

      // Auto-retry once for chunk errors
      if (this.state.retryCount === 0) {
        setTimeout(() => {
          this.handleRetry();
        }, 1000);
      }
    }
  }

  isChunkLoadError(error: Error): boolean {
    return (
      error.name === "ChunkLoadError" ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed") ||
      error.message.includes("failed to fetch") ||
      error.message.toLowerCase().includes("chunk")
    );
  }

  handleRetry = () => {
    this.setState(
      (prevState) => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
      }),
      () => {
        // Force a hard reload if retry count exceeds threshold
        if (this.state.retryCount > 2) {
          window.location.reload();
        }
      }
    );
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleRetry);
      }

      const isChunkError = this.isChunkLoadError(this.state.error);

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-surface border border-surface-border rounded-lg p-6 space-y-4">
            {/* Header */}
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-12 h-12 text-error" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-semibold text-white mb-2">
                  {isChunkError
                    ? "Update Detected"
                    : "Something Went Wrong"}
                </h1>
                <p className="text-muted">
                  {isChunkError
                    ? "The application has been updated. Please reload to get the latest version."
                    : "An unexpected error occurred. We apologize for the inconvenience."}
                </p>
              </div>
            </div>

            {/* Error Details */}
            <div className="bg-surface-dark rounded-lg p-4 border border-surface-border">
              <p className="text-sm font-semibold text-white mb-2">
                Error Details:
              </p>
              <p className="text-sm text-error font-mono break-all">
                {this.state.error.name}: {this.state.error.message}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={this.handleReload}
                className="flex-1"
                size="lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Application
              </Button>
              {!isChunkError && (
                <Button
                  onClick={this.handleRetry}
                  variant="outline"
                  className="flex-1"
                  size="lg"
                >
                  Try Again
                </Button>
              )}
            </div>

            {/* Retry Count Info */}
            {this.state.retryCount > 0 && (
              <p className="text-xs text-muted text-center">
                Retry attempts: {this.state.retryCount}
              </p>
            )}

            {/* Stack Trace (collapsed) */}
            {this.state.errorInfo && (
              <details className="bg-surface-dark rounded-lg p-4 border border-surface-border">
                <summary className="text-sm font-semibold text-white cursor-pointer hover:text-primary">
                  View Stack Trace
                </summary>
                <pre className="text-xs text-muted mt-3 overflow-x-auto whitespace-pre-wrap font-mono">
                  {this.state.errorInfo}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
